/**
 * Enviar uma ocorrência das creches aos encarregados: o texto (com os modelos prontos) e para
 * quem vai — todos os alunos, algumas crianças ou algumas salas. Aberto pelo `MainContext`
 * (o botão do cabeçalho do casco da Escola ou a lista de crianças, que já traz as crianças
 * ou as salas escolhidas).
 *
 * Pedidos: `GET ocorrencias/pesquisar`, `ocorrencias/pesquisar_salas`, `ocorrencias/turma/{id}`
 * e `POST ocorrencias/enviar` (todos pelo `basePath` da Escola).
 */
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Icon } from "@iconify/react";
import { Check, Loader2, Search, X } from "lucide-react";
import { AreaTexto, Avatar, Aviso, Botao, Entrada, Janela, Spinner, useJanela, useValorAtrasado } from "@/Logigate/ui";
import { cn } from "@/lib/utils";
import { track } from "@/utils/px";
import { showPop } from "@/utils/Pops";
import req from "@/utils/req";
import { useEscolaContext } from "../../hooks/useEscolaContext";
import ModelosOcorrencias, { type ModeloOcorrencia, textoDoModelo } from "@/Escola/componentes/ModelosOcorrencias";

interface ModalDialogOcorrenciasModernoProps {
    opened: boolean;
    onClose: () => void;
    criancasSelecionadas: any[];
    setCriancasSelecionadas: (criancas: any[]) => void;
    salasSelecionadas: any[];
    setSalasSelecionadas: (salas: any[]) => void;
}

interface FormData {
    tipo: string;
    texto: string;
    criancas: any[];
    salas: any[];
}

const MAXIMO_TEXTO = 3500;

const DESTINOS = [
    { value: "0", rotulo: "Todos os alunos", icone: "mdi:account-group-outline" },
    { value: "1", rotulo: "Algumas crianças", icone: "mdi:account-child-outline" },
    { value: "2", rotulo: "Algumas salas", icone: "mdi:google-classroom" },
];

const ModalDialogOcorrenciasModerno: React.FC<ModalDialogOcorrenciasModernoProps> = ({
    opened,
    onClose,
    criancasSelecionadas,
    setCriancasSelecionadas,
    salasSelecionadas,
    setSalasSelecionadas,
}) => {
    const { basePath, escola } = useEscolaContext();
    const idFormulario = useId();
    const [criancas, setCriancas] = useState<any[]>([]);
    const [salas, setSalas] = useState<any[]>([]);
    const [criancasLoading, setCriancasLoading] = useState(false);
    const [salasLoading, setSalasLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<ModeloOcorrencia | null>(null);

    // As outras crianças da turma da primeira criança escolhida (com cache por turma).
    const [criancasTurma, setCriancasTurma] = useState<any[]>([]);
    const [criancasTurmaLoading, setCriancasTurmaLoading] = useState(false);
    const turmaCache = useRef<Record<string, any[]>>({});

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        setError,
        clearErrors,
        watch,
        formState: { errors, isDirty },
    } = useForm<FormData>({
        defaultValues: {
            tipo: "0", // 0=Todos, 1=Crianças, 2=Salas
            texto: "",
            criancas: [],
            salas: [],
        },
    });

    const formValues = watch() || { tipo: "0", texto: "", criancas: [], salas: [] };
    const escolhidas: any[] = formValues.criancas || [];
    const salasEscolhidas: any[] = formValues.salas || [];

    // A pesquisa (2 letras no mínimo, meio segundo depois de parar de escrever).
    const [searchCriancas, setSearchCriancas] = useState("");
    const [searchSalas, setSearchSalas] = useState("");
    const debouncedSearchCriancas = useValorAtrasado(searchCriancas, 500);
    const debouncedSearchSalas = useValorAtrasado(searchSalas, 500);

    const aplicarTemplate = (template: ModeloOcorrencia) => {
        setValue("texto", textoDoModelo(template), { shouldDirty: true, shouldValidate: true });
        setSelectedTemplate(template);
    };

    const getCriancas = useCallback(async () => {
        setCriancasLoading(true);
        const response = await req().get(basePath(`ocorrencias/pesquisar?ano_lectivo=${escola?.ano?.id}&q=${encodeURIComponent(debouncedSearchCriancas)}`));
        if (response.data?.estado === "ok") {
            setCriancas(response.data.items || []);
        }
        setCriancasLoading(false);
    }, [debouncedSearchCriancas, basePath, escola?.ano?.id]);

    const getSalas = useCallback(async () => {
        setSalasLoading(true);
        const response = await req().get(basePath(`ocorrencias/pesquisar_salas?ano_lectivo=${escola?.ano?.id}&q=${encodeURIComponent(debouncedSearchSalas)}`));
        if (response.data?.estado === "ok") {
            setSalas(response.data.items || []);
        }
        setSalasLoading(false);
    }, [debouncedSearchSalas, basePath, escola?.ano?.id]);

    const getCriancasTurma = useCallback(async (turmaId: string | number) => {
        if (!turmaId) {
            return;
        }
        const turmaKey = turmaId.toString();
        if (turmaCache.current[turmaKey]) {
            setCriancasTurma(turmaCache.current[turmaKey]);
            return;
        }

        setCriancasTurmaLoading(true);
        const response = await req().get(basePath(`ocorrencias/turma/${turmaId}?ano_lectivo=${escola?.ano?.id}&limit=10`));
        if (response.data?.estado === "ok") {
            const items = response.data.items || [];
            setCriancasTurma(items);
            turmaCache.current[turmaKey] = items;
        }
        setCriancasTurmaLoading(false);
    }, [basePath, escola?.ano?.id]);

    useEffect(() => {
        if (debouncedSearchCriancas && debouncedSearchCriancas.length >= 2) {
            getCriancas();
        } else {
            setCriancas([]);
        }
    }, [debouncedSearchCriancas]);

    useEffect(() => {
        if (debouncedSearchSalas && debouncedSearchSalas.length >= 2) {
            getSalas();
        } else {
            setSalas([]);
        }
    }, [debouncedSearchSalas]);

    // As crianças que já vêm escolhidas (da lista): passa-se a «Algumas crianças» e
    // carregam-se as outras da mesma turma.
    useEffect(() => {
        if (criancasSelecionadas && criancasSelecionadas.length > 0) {
            setValue("tipo", "1");
            setValue("criancas", criancasSelecionadas);

            const primeiraCrianca = criancasSelecionadas[0];
            if (primeiraCrianca && primeiraCrianca.turma_id) {
                getCriancasTurma(primeiraCrianca.turma_id);
            }
        }
    }, [criancasSelecionadas, setValue, getCriancasTurma]);

    useEffect(() => {
        if (salasSelecionadas && salasSelecionadas.length > 0) {
            setValue("tipo", "2");
            setValue("salas", salasSelecionadas);
        }
    }, [salasSelecionadas, setValue]);

    const alternarCrianca = (crianca: any, marcar: boolean) => {
        const novas = marcar ? [...escolhidas, crianca] : escolhidas.filter((c) => c.id !== crianca.id);
        setValue("criancas", novas, { shouldDirty: true });
        clearErrors("criancas");
    };

    const alternarSala = (sala: any, marcar: boolean) => {
        const novas = marcar ? [...salasEscolhidas, sala] : salasEscolhidas.filter((s) => s.id !== sala.id);
        setValue("salas", novas, { shouldDirty: true });
        clearErrors("salas");
    };

    const handleClose = () => {
        setCriancasSelecionadas([]);
        setSalasSelecionadas([]);
        setSelectedTemplate(null);
        setCriancas([]);
        setSalas([]);
        setCriancasTurma([]);
        turmaCache.current = {};
        setSearchCriancas("");
        setSearchSalas("");
        reset();
        onClose();
    };

    const onSubmit = async (data: FormData) => {
        // O servidor também o exige (`required_if`); aqui avisa-se logo no campo.
        if (data.tipo === "1" && !(data.criancas || []).some((c) => c && c.id)) {
            setError("criancas", { type: "required", message: "Escolhe pelo menos uma criança." });
            return;
        }
        if (data.tipo === "2" && !(data.salas || []).some((s) => s && s.id)) {
            setError("salas", { type: "required", message: "Escolhe pelo menos uma sala." });
            return;
        }

        setLoading(true);
        const payload = {
            ...data,
            criancas: (data.criancas || []).filter((c) => c && c.id).map((c: any) => ({ id: c.id })),
            salas: (data.salas || []).filter((s) => s && s.id).map((s: any) => ({ id: s.id })),
        };

        const response = await req().post(basePath("ocorrencias/enviar"), payload);
        setLoading(false);

        if (response.data?.estado === "ok") {
            showPop("", "Ocorrência enviada.", "success");
            handleClose();
        } else {
            // Um 422 do Laravel traz a primeira mensagem em `message`.
            const texto = response.status === 422 && response.data?.message ? response.data.message : response.data?.texto;
            showPop("", texto || "Não foi possível enviar a ocorrência. Tenta de novo.", "error");
        }
    };

    if (!formValues || typeof formValues !== "object") {
        return null;
    }

    const turmaDaPrimeira = escolhidas[0]?.turma;
    const resultadosCriancas = criancas.filter((c) => c?.id && c?.nome && !criancasTurma.some((t) => t.id === c.id));

    return (
        <Janela
            aberta={opened}
            onFechar={handleClose}
            largura="lg"
            titulo="Enviar ocorrência"
            descricao="Escreve o texto e escolhe a quem se destina."
            sujo={isDirty}
            rodape={
                <>
                    <CancelarComRegisto disabled={loading} />
                    <Botao
                        type="submit"
                        form={idFormulario}
                        aCarregar={loading}
                        icone={<Icon icon="mdi:send" className="size-4" />}
                        className="h-11 flex-1 sm:h-9 sm:flex-none"
                    >
                        Enviar ocorrência
                    </Botao>
                </>
            }
        >
            <form id={idFormulario} onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                <ModelosOcorrencias onTemplateSelect={aplicarTemplate} selectedTemplate={selectedTemplate} />

                <div className="space-y-2">
                    {selectedTemplate && (
                        <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-portal-suave py-1 pl-3 pr-1 text-xs font-medium text-portal-texto dark:bg-portal-claro/10 dark:text-portal-claro">
                            <Icon icon="mdi:text-box-check-outline" className="size-4 shrink-0" />
                            <span className="truncate">Modelo: {selectedTemplate.nome}</span>
                            <BotaoTirar
                                rotulo="Tirar o modelo"
                                onClick={() => {
                                    setValue("texto", "", { shouldDirty: true });
                                    setSelectedTemplate(null);
                                }}
                            />
                        </span>
                    )}

                    <Controller
                        name="texto"
                        control={control}
                        rules={{ required: "Escreve o texto da ocorrência" }}
                        render={({ field }) => (
                            <AreaTexto
                                {...field}
                                rotulo="Texto da ocorrência"
                                obrigatorio
                                placeholder="Escreve o texto da ocorrência ou escolhe um modelo acima…"
                                linhas={8}
                                autoAjustar
                                linhasMax={15}
                                maxLength={MAXIMO_TEXTO}
                                descricao={`${field.value?.length || 0}/${MAXIMO_TEXTO} caracteres`}
                                erro={errors.texto?.message}
                            />
                        )}
                    />
                </div>

                <Controller
                    name="tipo"
                    control={control}
                    rules={{ required: "Escolhe a quem se destina" }}
                    render={({ field }) => (
                        <fieldset>
                            <legend className="mb-1.5 text-[13px] font-medium text-zinc-800 dark:text-zinc-200">
                                Enviar para<span className="ml-0.5 text-red-500" aria-hidden>*</span>
                            </legend>
                            <div className="grid grid-cols-3 gap-2">
                                {DESTINOS.map((d) => {
                                    const activo = field.value === d.value;
                                    return (
                                        <label
                                            key={d.value}
                                            className={cn(
                                                "relative flex min-h-[4.5rem] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-2.5 text-center transition-colors",
                                                "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-portal/40",
                                                activo
                                                    ? "border-portal bg-portal-suave/60 text-portal-texto dark:border-portal-claro/60 dark:bg-portal-claro/10 dark:text-portal-claro"
                                                    : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800",
                                            )}
                                        >
                                            <input
                                                type="radio"
                                                name={field.name}
                                                value={d.value}
                                                checked={activo}
                                                onChange={() => field.onChange(d.value)}
                                                onBlur={field.onBlur}
                                                className="sr-only"
                                            />
                                            <Icon icon={d.icone} className="size-6" aria-hidden />
                                            <span className="text-[13px] font-medium leading-tight">{d.rotulo}</span>
                                            {activo && (
                                                <span className="absolute right-1.5 top-1.5 flex size-4 items-center justify-center rounded-full bg-portal text-white dark:bg-portal-claro dark:text-zinc-950">
                                                    <Check className="size-3" strokeWidth={3} aria-hidden />
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                            {errors.tipo?.message && <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{errors.tipo.message}</p>}
                        </fieldset>
                    )}
                />

                {formValues.tipo === "1" && (
                    <section className="space-y-3" aria-label="Crianças">
                        <CabecalhoEscolha titulo="Crianças" contagem={escolhidas.length} />

                        {escolhidas.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {escolhidas.map((crianca) => (
                                    <span key={crianca.id} className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-portal-suave py-1 pl-1 pr-1 text-xs font-medium text-portal-texto dark:bg-portal-claro/10 dark:text-portal-claro">
                                        <Avatar src={crianca.foto_link} nome={crianca.nome} tamanho="xs" />
                                        <span className="truncate">{crianca.nome}</span>
                                        <BotaoTirar rotulo={`Tirar ${crianca.nome}`} onClick={() => alternarCrianca(crianca, false)} />
                                    </span>
                                ))}
                            </div>
                        )}

                        <div data-nao-suja>
                            <Entrada
                                placeholder="Pesquisar crianças pelo nome…"
                                aria-label="Pesquisar crianças pelo nome"
                                value={searchCriancas}
                                onChange={(e) => setSearchCriancas(e.target.value)}
                                icone={<Search />}
                                iconeDireita={(criancasLoading || criancasTurmaLoading) ? <Loader2 className="animate-spin text-portal" /> : undefined}
                                autoComplete="off"
                                tamanho="lg"
                                className="sm:h-10 sm:text-sm"
                            />
                        </div>

                        <div className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 dark:divide-zinc-800 dark:border-zinc-800">
                            {criancasTurma.length > 0 && (
                                <GrupoOpcoes titulo={`Da mesma turma${turmaDaPrimeira ? ` (${turmaDaPrimeira})` : ""}`}>
                                    {criancasTurma.filter((c) => c?.id && c?.nome).map((crianca) => (
                                        <LinhaOpcao
                                            key={`turma-${crianca.id}`}
                                            marcada={escolhidas.some((c) => c.id === crianca.id)}
                                            onMudar={(m) => alternarCrianca(crianca, m)}
                                            titulo={crianca.nome}
                                            detalhe={crianca.turma || "Sem turma"}
                                            imagem={<Avatar src={crianca.foto_link} nome={crianca.nome} tamanho="sm" />}
                                        />
                                    ))}
                                </GrupoOpcoes>
                            )}

                            {searchCriancas.length >= 2 && (
                                <GrupoOpcoes titulo="Resultados da pesquisa">
                                    {criancasLoading ? (
                                        <div className="flex justify-center py-5"><Spinner /></div>
                                    ) : resultadosCriancas.length > 0 ? (
                                        resultadosCriancas.map((crianca) => (
                                            <LinhaOpcao
                                                key={`pesquisa-${crianca.id}`}
                                                marcada={escolhidas.some((c) => c.id === crianca.id)}
                                                onMudar={(m) => alternarCrianca(crianca, m)}
                                                titulo={crianca.nome}
                                                detalhe={crianca.turma || "Sem turma"}
                                                imagem={<Avatar src={crianca.foto_link} nome={crianca.nome} tamanho="sm" />}
                                            />
                                        ))
                                    ) : (
                                        <SemOpcoes>Nenhuma criança encontrada.</SemOpcoes>
                                    )}
                                </GrupoOpcoes>
                            )}

                            {searchCriancas.length < 2 && criancasTurma.length === 0 && (
                                <SemOpcoes>Escreve pelo menos 2 letras para pesquisar.</SemOpcoes>
                            )}
                        </div>
                        {errors.criancas?.message && <p className="text-xs text-red-600 dark:text-red-400" role="alert">{String(errors.criancas.message)}</p>}
                    </section>
                )}

                {formValues.tipo === "2" && (
                    <section className="space-y-3" aria-label="Salas">
                        <CabecalhoEscolha titulo="Salas" contagem={salasEscolhidas.length} />

                        {salasEscolhidas.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {salasEscolhidas.map((sala) => (
                                    <span key={sala.id} className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-portal-suave py-1 pl-3 pr-1 text-xs font-medium text-portal-texto dark:bg-portal-claro/10 dark:text-portal-claro">
                                        <Icon icon="mdi:google-classroom" className="size-3.5 shrink-0" aria-hidden />
                                        <span className="truncate">{sala.nome || sala.desturma}</span>
                                        <BotaoTirar rotulo={`Tirar ${sala.nome || sala.desturma}`} onClick={() => alternarSala(sala, false)} />
                                    </span>
                                ))}
                            </div>
                        )}

                        <div data-nao-suja>
                            <Entrada
                                placeholder="Pesquisar salas pelo nome…"
                                aria-label="Pesquisar salas pelo nome"
                                value={searchSalas}
                                onChange={(e) => setSearchSalas(e.target.value)}
                                icone={<Search />}
                                iconeDireita={salasLoading ? <Loader2 className="animate-spin text-portal" /> : undefined}
                                autoComplete="off"
                                tamanho="lg"
                                className="sm:h-10 sm:text-sm"
                            />
                        </div>

                        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800">
                            {searchSalas.length >= 2 ? (
                                <GrupoOpcoes titulo="Salas encontradas">
                                    {salasLoading ? (
                                        <div className="flex justify-center py-5"><Spinner /></div>
                                    ) : salas.filter((s) => s?.id && (s?.nome || s?.desturma)).length > 0 ? (
                                        salas.filter((s) => s?.id && (s?.nome || s?.desturma)).map((sala) => (
                                            <LinhaOpcao
                                                key={`sala-${sala.id}`}
                                                marcada={salasEscolhidas.some((s) => s.id === sala.id)}
                                                onMudar={(m) => alternarSala(sala, m)}
                                                titulo={sala.nome || sala.desturma}
                                                detalhe={sala.classe || "Sem classe"}
                                                imagem={
                                                    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                                                        <Icon icon="mdi:google-classroom" className="size-4" />
                                                    </span>
                                                }
                                            />
                                        ))
                                    ) : (
                                        <SemOpcoes>Nenhuma sala encontrada.</SemOpcoes>
                                    )}
                                </GrupoOpcoes>
                            ) : (
                                <SemOpcoes>Escreve pelo menos 2 letras para pesquisar salas.</SemOpcoes>
                            )}
                        </div>
                        {errors.salas?.message && <p className="text-xs text-red-600 dark:text-red-400" role="alert">{String(errors.salas.message)}</p>}
                    </section>
                )}

                {formValues.tipo !== "0" && (
                    <Aviso cor="marca" icone={<Icon icon="mdi:information-outline" />} titulo="Resumo do envio">
                        {formValues.tipo === "1" && <>Será enviado para {escolhidas.length} criança(s) escolhida(s).</>}
                        {formValues.tipo === "2" && <>Será enviado para {salasEscolhidas.length} sala(s) escolhida(s).</>}
                    </Aviso>
                )}
            </form>
        </Janela>
    );
};

export default ModalDialogOcorrenciasModerno;

/** O «Cancelar» do rodapé: regista o clique e fecha (a perguntar, se houver texto). */
function CancelarComRegisto({ disabled }: { disabled?: boolean }) {
    const { tentarFechar } = useJanela();
    return (
        <Botao
            variante="neutro"
            disabled={disabled}
            className="h-11 flex-1 sm:h-9 sm:flex-none"
            onClick={() => {
                track("click", "escola_ocorrencias_cancelar", {});
                tentarFechar();
            }}
        >
            Cancelar
        </Botao>
    );
}

function CabecalhoEscolha({ titulo, contagem }: { titulo: string; contagem: number }) {
    return (
        <div className="flex items-center justify-between">
            <p className="text-[13px] font-medium text-zinc-800 dark:text-zinc-200">
                {titulo}<span className="ml-0.5 text-red-500" aria-hidden>*</span>
            </p>
            {contagem > 0 && <span className="text-xs text-zinc-500 dark:text-zinc-400">{contagem} escolhida{contagem === 1 ? "" : "s"}</span>}
        </div>
    );
}

/** O X de uma ficha escolhida, com a área de toque alargada para os 44 px. */
function BotaoTirar({ rotulo, onClick }: { rotulo: string; onClick: () => void }) {
    return (
        <button
            type="button"
            aria-label={rotulo}
            title={rotulo}
            onClick={onClick}
            className="relative flex size-6 shrink-0 items-center justify-center rounded-full transition-colors after:absolute after:-inset-2.5 hover:bg-portal/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-portal/40 dark:hover:bg-portal-claro/20"
        >
            <X className="size-3.5" />
        </button>
    );
}

function GrupoOpcoes({ titulo, children }: { titulo: string; children: React.ReactNode }) {
    return (
        <div className="p-1.5">
            <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{titulo}</p>
            <div>{children}</div>
        </div>
    );
}

function LinhaOpcao({ marcada, onMudar, titulo, detalhe, imagem }: {
    marcada: boolean;
    onMudar: (marcada: boolean) => void;
    titulo: React.ReactNode;
    detalhe?: React.ReactNode;
    imagem?: React.ReactNode;
}) {
    return (
        <label
            className={cn(
                "flex min-h-12 cursor-pointer items-center gap-3 rounded-lg px-2.5 py-1.5 transition-colors",
                marcada ? "bg-portal-suave/50 dark:bg-portal-claro/5" : "hover:bg-zinc-50 dark:hover:bg-zinc-800/60",
            )}
        >
            <input
                type="checkbox"
                checked={marcada}
                onChange={(e) => onMudar(e.target.checked)}
                className="size-4 shrink-0 cursor-pointer rounded border-zinc-300 text-portal focus:ring-2 focus:ring-portal/30 focus:ring-offset-0 dark:border-zinc-600 dark:bg-zinc-900"
            />
            {imagem}
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-zinc-900 dark:text-zinc-100">{titulo}</span>
                {detalhe && <span className="block truncate text-xs text-zinc-500 dark:text-zinc-400">{detalhe}</span>}
            </span>
        </label>
    );
}

function SemOpcoes({ children }: { children: React.ReactNode }) {
    return <p className="px-4 py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">{children}</p>;
}
